import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton } from "@koclukpanel/ui";
import { googleMeetMi, meetBaglantisiSemasi } from "@koclukpanel/contracts";
import { ApiHatasi, gorusmeGuncelle, type KoclukGorusmesi } from "./api";

/**
 * GÖRÜŞME (MEET) BAĞLANTISI — F-SES-02b (Emre 06.10.2026, ürün incelemesi H2)
 *
 * API alanı (`meetUrl`) baştan beri vardı; panel formu göndermiyor, öğrenci
 * ekranı çizmiyordu. Doğrulama sunucuyla AYNI şemadan (`meetBaglantisiSemasi`,
 * yalnız https) — iki ayrı kural günün birinde ayrışırdı.
 *
 * Görünürlük sunucuda: veli bağlantıyı yalnız veli görüşmesinde alır
 * (`meetBaglantisiGorunurMu`). Bu bileşen personel ekranında.
 */

/** Boş alan geçerli (isteğe bağlı); doluysa şema hatası ya da `null`. */
export function meetBaglantisiHatasi(deger: string): string | null {
  if (deger.trim() === "") return null;
  const r = meetBaglantisiSemasi.safeParse(deger);
  return r.success ? null : (r.error.issues[0]?.message ?? "Geçerli bir bağlantı girin.");
}

export function MeetAlani({
  deger,
  onDegisti,
  hata,
}: {
  deger: string;
  onDegisti: (d: string) => void;
  hata: string | null;
}) {
  const ipucu =
    deger.trim() !== "" && hata === null && !googleMeetMi(deger)
      ? "Google Meet dışında bir adres — öğrenci bu bağlantıyı açacak."
      : "İsteğe bağlı. https:// ile başlamalı (tercihen meet.google.com).";
  return (
    <Alan
      etiket="Meet bağlantısı"
      type="url"
      inputMode="url"
      placeholder="https://meet.google.com/…"
      value={deger}
      onChange={(e) => onDegisti(e.target.value)}
      hata={hata ?? undefined}
      yardim={ipucu}
      autoComplete="off"
    />
  );
}

export function MeetBaglantisi({
  gorusme,
  ogrenciId,
}: {
  gorusme: KoclukGorusmesi;
  ogrenciId: string;
}) {
  const qc = useQueryClient();
  const [duzenle, setDuzenle] = useState(false);
  const [deger, setDeger] = useState(gorusme.meetUrl ?? "");
  const hata = meetBaglantisiHatasi(deger);
  // Raporu tamamlanmış görüşme düzenlenemiyor (sunucu KAPALI_KAYIT).
  const duzenlenebilir = gorusme.status !== "REPORT_DONE";

  const kaydet = useMutation({
    mutationFn: () => gorusmeGuncelle(gorusme.id, { meetUrl: deger.trim() || null }),
    onSuccess: async () => {
      setDuzenle(false);
      await qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
    },
  });

  if (duzenle) {
    return (
      <div className="flex flex-col gap-2">
        <MeetAlani deger={deger} onDegisti={setDeger} hata={hata} />
        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Bağlantı kaydedilemedi."}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={kaydet.isPending}
            disabled={hata !== null}
            onClick={() => kaydet.mutate()}
          >
            Bağlantıyı kaydet
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setDuzenle(false)}>
            Vazgeç
          </Buton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {gorusme.meetUrl ? (
        <a
          href={gorusme.meetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-marka-700 font-medium underline"
        >
          Görüşmeye katıl
        </a>
      ) : (
        <span className="text-notr-600">Görüşme bağlantısı eklenmemiş.</span>
      )}
      {duzenlenebilir && (
        <Buton gorunum="sessiz" onClick={() => setDuzenle(true)}>
          {gorusme.meetUrl ? "Bağlantıyı değiştir" : "Bağlantı ekle"}
        </Buton>
      )}
    </div>
  );
}
